import { useEffect, useState } from "react";
import Navbar from "./components/Navbar";
import Inicio from "./components/Inicio";
import Protocolo from "./components/Protocolo";
import Aranceles from "./components/Aranceles";
import Beneficios from "./components/Beneficios";
import Descargas from "./components/Descargas";
import OdontologosTratantes from "./components/OdontologosTratantes";
import IngresoSolicitudes from "./components/IngresoSolicitudes";
import Contacto from "./components/Contacto";
import PrivacyPolicy from "./components/PrivacyPolicy";

const PAGES = {
  inicio: Inicio,
  protocolo: Protocolo,
  aranceles: Aranceles,
  odontologos: OdontologosTratantes,
  beneficios: Beneficios,
  descargas: Descargas,
  solicitudes: IngresoSolicitudes,
  privacidad: PrivacyPolicy,
};

const HASH_TO_PAGE = {
  "#/": "inicio",
  "#/inicio": "inicio",
  "#/protocolo": "protocolo",
  "#/aranceles": "aranceles",
  "#/odontologos": "odontologos",
  "#/beneficios": "beneficios",
  "#/descargas": "descargas",
  "#/solicitudes": "solicitudes",
  "#/privacy-policy": "privacidad",
  "#/politica-de-privacidad": "privacidad",
};

const PAGE_TO_HASH = {
  inicio: "#/inicio",
  protocolo: "#/protocolo",
  aranceles: "#/aranceles",
  odontologos: "#/odontologos",
  beneficios: "#/beneficios",
  descargas: "#/descargas",
  solicitudes: "#/solicitudes",
  privacidad: "#/privacy-policy",
};

function getPageFromHash(hash) {
  return HASH_TO_PAGE[hash] ?? "inicio";
}

export default function App() {
  const [activePage, setActivePage] = useState(() => getPageFromHash(window.location.hash));

  useEffect(() => {
    const onHashChange = () => {
      setActivePage(getPageFromHash(window.location.hash));
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener("hashchange", onHashChange);

    if (!window.location.hash) {
      window.history.replaceState(null, "", PAGE_TO_HASH.inicio);
    }

    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const handleNav = (pageId) => {
    setActivePage(pageId);
    const targetHash = PAGE_TO_HASH[pageId] ?? PAGE_TO_HASH.inicio;
    if (window.location.hash !== targetHash) {
      window.history.pushState(null, "", targetHash);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const PageComponent = PAGES[activePage] || Inicio;

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar active={activePage} onNav={handleNav} />

      <main>
        {/* Inicio always shows hero at top */}
        {activePage === "inicio" && (
          <>
            <Inicio onNav={handleNav} />

            {/* Quick access cards on home */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-6 md:px-8 py-6 max-w-5xl mx-auto">
              {[
                {
                  id: "protocolo",
                  icon: "📋",
                  title: "Protocolos",
                  desc: "Flujo de solicitud, aprobación y pasos para reembolso dental.",
                  bg: "bg-codelco/5 border-codelco/20",
                },
                {
                  id: "aranceles",
                  icon: "🧾",
                  title: "Aranceles",
                  desc: "Valores de referencia del convenio para procedimientos dentales.",
                  bg: "bg-codelco/5 border-codelco/20",
                },
                {
                  id: "beneficios",
                  icon: "💰",
                  title: "Beneficios Codelco",
                  desc: "Convenio Colectivo 2013–2017: 85% de reembolso dental.",
                  bg: "bg-navy-600/5 border-navy-600/15",
                },
                {
                  id: "descargas",
                  icon: "📥",
                  title: "Descargas y Links",
                  desc: "Formularios, videos tutoriales y recursos del proceso.",
                  bg: "bg-gray-50 border-gray-200",
                },
                {
                  id: "odontologos",
                  icon: "👨‍⚕️",
                  title: "Odontólogos Tratantes",
                  desc: "Listado de profesionales habilitados para atención Codelco.",
                  bg: "bg-gray-50 border-gray-200",
                },
                {
                  id: "solicitudes",
                  icon: "🖥",
                  title: "Ingreso de Solicitudes",
                  desc: "Accede a la plataforma de ayuda dental con tu SAP y RUT.",
                  bg: "bg-gray-50 border-gray-200",
                },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={`text-left border rounded-xl p-5 hover:shadow-md transition-all group ${item.bg}`}
                >
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <div className="text-sm font-semibold text-navy-600 mb-1 group-hover:text-codelco transition-colors">
                    {item.title}
                  </div>
                  <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
                </button>
              ))}
            </div>

            <Contacto onNav={handleNav} />
          </>
        )}

        {/* Other pages */}
        {activePage !== "inicio" && (
          <>
            <PageComponent onNav={handleNav} />
            <Contacto onNav={handleNav} />
          </>
        )}
      </main>
    </div>
  );
}
